/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import _ from 'lodash';
import validate from 'validate.js';

import * as constants from './constants';

const ARRAY = 'array';

export const strings = {
  DUPLICATE_ITEM: 'duplicate item'
};

validate.isEmpty = function isEmpty(value) {
  // Null and undefined are empty
  if (!validate.isDefined(value)) {
    return true;
  }

  // functions are non empty
  if (validate.isFunction(value)) {
    return false;
  }

  // Arrays and strings use the length attribute
  if (validate.isString(value) || validate.isArray(value)) {
    return value.length === 0;
  }

  // Dates have no attributes but aren't empty
  if (validate.isDate(value)) {
    return false;
  }

  // If we find at least one property we consider it non empty
  if (validate.isObject(value)) {
    return Object.keys(value).length === 0;
  }

  return false;
};

validate.validators.noWhitespace = function (value) {
  if (value.length > 0) {
    return value.trim().length === 0 ? 'cannot be just whitespaces' : null;
  }
};

validate.validators.length.options = {
  tooLong: 'is too long. The maximum length is %{count} characters.'
};

validate.validators.presence.message = 'cannot be empty.';

validate.validators.entityNameValidator = function entityName(
  value,
  _options,
  _key,
  attributes
) {
  if (attributes.source === constants.PREBUILT_CONTENT_WORKSPACE_NAME) {
    return validate.single(value, {
      format: {
        pattern: constants.SYSTEM_ENTITY_NAME_REGEX,
        message: constants.SYSTEM_ENTITY_NAME_ERROR
      }
    });
  } else {
    var sysPrefixMatch = validate.single(value, {
      format: {
        pattern: constants.SYSTEM_PREFIX,
        message: constants.SYSTEM_PREFIX_ERROR
      }
    });

    if (sysPrefixMatch) {
      return sysPrefixMatch;
    } else {
      return validate.single(value, {
        format: {
          pattern: constants.ENTITY_NAME_REGEX,
          message: constants.ENTITY_NAME_ERROR
        }
      });
    }
  }
};

validate.validators.systemEntityNameValidator = function entityName(
  value,
  _option
) {
  return validate.single(value, {
    format: {
      pattern: constants.SYSTEM_ENTITY_NAME_REGEX,
      message: constants.SYSTEM_ENTITY_NAME_ERROR
    }
  });
};

validate.validators.intentNameValidator = function intentName(
  value,
  _options,
  _key,
  _attributes
) {
  var sysPrefixMatch = validate.single(value, {
    format: {
      pattern: constants.SYSTEM_PREFIX,
      message: constants.SYSTEM_PREFIX_ERROR
    }
  });

  if (sysPrefixMatch) {
    return sysPrefixMatch;
  } else {
    return validate.single(value, {
      format: {
        pattern: constants.INTENT_NAME_REGEX,
        message: constants.INTENT_NAME_ERROR
      }
    });
  }
};
// helper functions to reduce eslint complexity
function handleArray(target, error) {
  for (const elem of target) {
    checkCondition(elem, error);
  }
}

function handleAndOrOperator(target, keys, error) {
  if (target[keys[0]].length >= 1) {
    // and/or can have min one item
    checkCondition(target[keys[0]], error);
  } else {
    error.push('has incorrect format');
  }
}

function handleConditionWithOperators(keys, target, error, targetValues) {
  if (
    keys.includes(constants.OPERATOR_TYPES.and) ||
    keys.includes(constants.OPERATOR_TYPES.and)
  ) {
    handleAndOrOperator(target, keys, error);
  } else if (_.isArray(targetValues)) {
    if (targetValues.length >= 2) {
      checkCondition(targetValues, error);
    } else {
      error.push('has incorrect format');
    }
  }
}

function handleObject(target, error) {
  const keys = Object.keys(target);
  if (!keys.includes('undefined')) {
    const targetValues: string[] = _.flatten(Object.values(target));
    if (
      keys.every(key => constants.OPERATOR_LIST.includes(key)) &&
      keys.length === constants.MAX_CONDITION_OPERATORS
    ) {
      handleConditionWithOperators(keys, target, error, targetValues);
    } else if (
      targetValues.some(value => !_.isString(value)) &&
      !targetValues.includes('defined')
    ) {
      // other values like from_entity or value
      error.push('has incorrect format');
    }
  } else {
    error.push('has incorrect format');
  }
}

// traverse condition and validate
function checkCondition(target, error) {
  if (error.length > 0) {
    return error;
  }
  if (_.isArray(target)) {
    handleArray(target, error);
  } else if (_.isObject(target)) {
    handleObject(target, error);
  }
  return;
}

function isJSONValid(condition) {
  try {
    JSON.stringify(condition);
    return true; // valid
  } catch (e) {
    return false; // not valid
  }
}

validate.validators.conditionFormatValidator =
  function conditionFormatValidator(condition) {
    if (!validate.isDefined(condition)) {
      return null;
    }
    if (!validate.isObject(condition)) {
      return null;
    }

    const errList = [];
    if (isJSONValid(condition)) {
      checkCondition(condition, errList);
    } else {
      errList.push('has incorrect format');
    }

    return errList.length ? errList : null;
  };

validate.validators.keys_inclusion = function keysInclusion(
  value,
  options,
  _key,
  attributes
) {
  if (!validate.isDefined(value)) {
    return null;
  }

  if (typeof attributes != constants.OBJECT || !_.isObject(options)) {
    return null;
  }
  const { within, message = constants.UNSUPPORTED_PROPERTIES } = options as any;

  const objectKeys = Object.keys(value);
  const diff = _.without(objectKeys, ...within);

  return diff.length !== 0 ? `${message} ${diff.join(', ')}` : null;
};

validate.validators.required = function type(
  value,
  options,
  _key,
  _attributes
) {
  if (options === true && !validate.isDefined(value)) {
    return 'is required';
  }
};

validate.validators.types = function types(value, options, _key, _attributes) {
  if (!validate.isDefined(value) || !_.isArray(options)) {
    return null;
  }

  for (let i = 0; i < options.length; i++) {
    const expectedType = options[i];
    if (!validate.validators.type.checks[expectedType]) {
      throw new Error('Could not find validator for type ' + expectedType);
    }
    if (validate.validators.type.checks[expectedType](value)) {
      return null;
    }
  }
  return 'must be of type "' + options.join(' or ') + '"';
};

validate.validators.type = function type(value, options, _key, _attributes) {
  // allow empty values by default (needs to be checked by "presence" check)
  if (!validate.isDefined(value)) {
    return null;
  }

  // allow defining object of any type using their constructor. --> options = {clazz: ClassName}
  if (typeof options === constants.OBJECT && options.clazz) {
    return value instanceof options.clazz
      ? null
      : 'must be of type "' + options.clazz.name + '"';
  }

  if (!validate.validators.type.checks[options]) {
    throw new Error('Could not find validator for type ' + options);
  }
  return validate.validators.type.checks[options](value)
    ? null
    : 'must be of type "' + options + '"';
};

validate.validators.type.checks = {
  object: validate.isHash,
  array: validate.isArray,
  integer: validate.isInteger,
  number: validate.isNumber,
  string: validate.isString,
  date: validate.isDate,
  boolean: validate.isBoolean,
  function: validate.isFunction
};

validate.validators.ifType = function ifType(
  value,
  options,
  _key,
  _attributes
) {
  if (!validate.isDefined(value) || !_.isObject(options)) {
    return null;
  }
  const types = Object.keys(options);

  const type = types.find(type => {
    return typeOf(value) === type;
  });

  if (!validate.isDefined(type)) {
    return null;
  }

  if (!validate.validators.type.checks[type]) {
    throw new Error('Could not find validator for type ' + type);
  }

  const validator = options[type];

  if (typeof validator !== constants.FUNCTION) {
    return null;
  }

  return validator(value);
};

/*eslint complexity: [2, 7] */
validate.validators.each = function each(
  value,
  options,
  _key,
  _attributes,
  globalOptions
) {
  if (!validate.isDefined(value)) {
    return null;
  } else if (!validate.isArray(value)) {
    return 'must be an array.';
  } else if (
    typeof options !== constants.OBJECT ||
    !options.validator ||
    typeof options.validator !== constants.FUNCTION
  ) {
    return null;
  }

  var errors = [];
  var numErrors = 0;
  _.forEach(value, function validateEach(data) {
    var error = options.validator(data, globalOptions);
    errors.push(error);
    if (error) {
      numErrors++;
    }
  });
  return numErrors > 0 ? errors : null;
};

const areArrayItemsUnique = (value, options) => {
  let keys = [];
  if (_.isObject(options) && (options as any).keys) {
    keys = (options as any).keys;
  }

  return !value.some((arrItem, index1) => {
    return value.some((arrItem2, index2) => {
      if (index2 <= index1) {
        return false;
      }

      if (keys.length === 0) {
        return _.isEqual(arrItem, arrItem2);
      } else {
        return keys.some(key => {
          return _.isEqual(arrItem[key], arrItem2[key]);
        });
      }
    });
  });
};

const assembleUniquenessErrorResult = (unique, options) => {
  if (!unique && options.message) {
    return options.message;
  } else if (!unique) {
    return strings.DUPLICATE_ITEM;
  }
  return null;
};

validate.validators.uniqueItems = function (value, options) {
  if (!validate.isDefined(value) || options === false) {
    return null;
  } else if (!validate.isArray(value)) {
    return 'must be an array.';
  }

  const unique = areArrayItemsUnique(value, options);

  return assembleUniquenessErrorResult(unique, options);
};

function typeOf(value) {
  if (_.isArray(value)) {
    return ARRAY;
  } else {
    return typeof value;
  }
}

// The original single function automatically sets the error formatting to flat and fullMessages to false
// This extended version internally calls the standard validate function with a wrapped value if non-flat formatting or fullMessages set to true are passed in

validate.extend(validate, {
  single: function (value, constraints, options) {
    if (
      !options ||
      ((!options.hasOwnProperty('format') || options.format === 'flat') &&
        (!options.hasOwnProperty('fullMessages') ||
          options.fullMessages === false))
    ) {
      const singleOptions = validate.extend(
        {},
        validate.single.options,
        options,
        {
          format: 'flat',
          fullMessages: false
        }
      );
      return validate(
        { single: value },
        { single: constraints },
        singleOptions
      );
    }

    const itemToValidate = {
      root: value
    };

    const itemConstraints = {
      root: constraints
    };

    return validate(itemToValidate, itemConstraints, options);
  }
});

export default validate;
